import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { personaListQuery } from '@xdocs/schemas';
import type { ErrorAlert, Persona, PersonaListResult } from '@xdocs/schemas';
import type { DataTableQuery } from '@xdocs/ui';
import { useWs } from '../../ws/WsProvider.js';

const DEFAULT_QUERY: DataTableQuery = { page: 1, pageSize: 25, sort: 'createdAt', dir: 'desc' };

export interface UsePersonasListResult {
  rows: Persona[];
  total: number | null;
  nextCursor: string | null;
  query: DataTableQuery;
  onQueryChange: (next: DataTableQuery) => void;
  onLoadMore: () => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  loadMoreError: string | undefined;
  onRetry: () => void;
  invalidate: () => void;
}

/** F16 `/personas` gallery — server-side pagination over `persona.list`,
 * scoped `mine`/`public` (the two `<Tabs>` on the page), same
 * `<DataTable>`-bridging shape as `useSharesList`. */
export function usePersonasList(scope: 'mine' | 'public'): UsePersonasListResult {
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState<DataTableQuery>(DEFAULT_QUERY);
  const [rows, setRows] = useState<Persona[]>([]);
  const appendModeRef = useRef(false);
  const enabled = connectionState === 'connected';
  const queryKey = useMemo(() => ['personas', 'list', scope, query] as const, [scope, query]);

  const result = useQuery<PersonaListResult, ErrorAlert>({
    queryKey,
    queryFn: () => requestTyped(wsClient, 'persona.list', personaListQuery.parse({ ...query, scope })),
    enabled,
  });

  useEffect(() => {
    if (!result.data) return;
    setRows((prev) => (appendModeRef.current ? [...prev, ...result.data.rows] : result.data.rows));
  }, [result.data]);

  // Reset accumulated rows when switching tabs (scope) so "mine" rows never
  // bleed into a freshly-selected "public" tab while its first page loads.
  useEffect(() => {
    setRows([]);
    appendModeRef.current = false;
    setQuery(DEFAULT_QUERY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const onQueryChange = useCallback((next: DataTableQuery) => {
    appendModeRef.current = false;
    setQuery(next);
  }, []);

  const onLoadMore = useCallback(() => {
    appendModeRef.current = true;
    setQuery((q) => ({ ...q, page: q.page + 1 }));
  }, []);

  const invalidate = useCallback(() => void queryClient.invalidateQueries({ queryKey: ['personas', 'list'] }), [queryClient]);

  const total = result.data?.total ?? null;
  const hasMore = total !== null ? rows.length < total : false;
  const isAppendFetch = appendModeRef.current && result.isFetching;

  return {
    rows,
    total,
    nextCursor: hasMore ? 'more' : null,
    query,
    onQueryChange,
    onLoadMore,
    isLoading: result.isLoading && !appendModeRef.current,
    isLoadingMore: isAppendFetch,
    isError: result.isError,
    loadMoreError: appendModeRef.current && result.isError ? result.error?.message : undefined,
    onRetry: () => void result.refetch(),
    invalidate,
  };
}
